import React from 'react';
import { 
  ShieldCheck, BookOpen, GraduationCap, Layers, 
  ArrowRight, Check, Award, Clock, Activity, Users,
  ClipboardList, CheckCircle2, ChevronRight, FileText,
  Sparkles
} from 'lucide-react';
import { useAuth } from './AuthBridge';
import { AdUnit } from './AdUnit';
import { ContinueLastQuizCard } from './ContinueLastQuizCard';

interface ClinicalTrustDashboardProps {
  onNavigate: (page: string) => void;
  standingData?: any;
}

export const ClinicalTrustDashboard: React.FC<ClinicalTrustDashboardProps> = ({ onNavigate, standingData }) => {
  const { user, activeCert } = useAuth();

  const certName = activeCert?.shortName || activeCert?.id?.toUpperCase() || 'CNA';
  const certFullName = activeCert?.name || 'Certified Nursing Assistant';
  const stateName = user?.state && user.state !== 'Global' ? user.state : 'State Board';

  // Dynamic Metrics calculations
  const totalExams = user?.history?.length || 0;
  const highestScore = user?.history && user.history.length > 0 
    ? Math.max(...user.history.map((h: any) => h.score || 0)) 
    : 0;
  const readinessPercent = standingData?.forecastScore ?? (user?.stats?.averageScore ? Math.round(user.stats.averageScore) : (user?.stats?.points ? Math.min(85, Math.round(user.stats.points / 20)) : 0));
  const rawStudyHours = user?.stats?.studyTimeMinutes ? (user.stats.studyTimeMinutes / 60) : (user?.stats?.studyHours || 0);
  const studyHours = Number.isInteger(rawStudyHours) ? rawStudyHours : Number(rawStudyHours).toFixed(2);
  const currentStreak = user?.stats?.streakDays || user?.stats?.streakCount || 0;
  const badgesCount = user?.stats?.badges?.length || (user?.stats?.points ? Math.floor(user.stats.points / 150) : 0);

  // Dynamic Pathway Progress
  const guideProgress = Math.min(100, Math.round(((user?.stats?.quizzesTaken || 0) * 8) + (user?.history?.length ? 15 : 0)));
  const practiceProgress = Math.min(100, Math.round((user?.stats?.quizzesTaken || 0) * 12));
  const skillsProgress = user?.stats?.skillsMastered ? Math.min(100, user.stats.skillsMastered * 10) : 0;

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto text-slate-900 pb-16 font-sans">
      
      {/* Top Bar: Breadcrumb + Board Certification Stamp */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
          <span className="text-slate-600 font-medium">Home</span>
          <ChevronRight size={14} className="text-slate-400" />
          <span className="text-slate-900 font-extrabold">Dashboard</span>
        </div>

        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 text-[#0F7D6C] rounded-full text-xs font-bold border border-emerald-200/80 shadow-xs">
          <CheckCircle2 size={14} className="text-[#0F7D6C]" />
          <span>{stateName} Board Certified Standard</span>
        </div>
      </div>

      {/* Continue In-Progress Quiz / Exam Banner */}
      <ContinueLastQuizCard onNavigate={onNavigate} />

      {/* Main Hero Header Box + Right Column Ad */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        <div className="xl:col-span-8 bg-[#0B2942] text-white rounded-3xl p-6 sm:p-8 md:p-10 shadow-xl relative overflow-hidden border border-slate-800 flex flex-col justify-between">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-center relative z-10">
            
            <div className="lg:col-span-7 space-y-6">
              {/* Faculty Stamp Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/15 text-amber-300 text-[11px] font-extrabold tracking-wider rounded-md border border-amber-400/30">
                <span className="text-amber-400">◆</span>
                <span>DNP & RN FACULTY-BUILT</span>
              </div>

              {/* Main Headline */}
              <div className="space-y-3">
                <h1 className="text-2xl md:text-4xl font-serif font-normal text-white leading-snug">
                  Welcome back, {user?.name || 'Diamond'}. <br />
                  <span className="text-emerald-300 font-bold underline decoration-emerald-400/50 decoration-2 underline-offset-4">
                    You're {readinessPercent}% ready
                  </span> for your {stateName} {certName} board exam.
                </h1>
                <p className="text-slate-300 text-xs md:text-sm max-w-2xl leading-relaxed font-normal opacity-90">
                  Every question, rationale, and skill checklist on this platform is written and peer-reviewed by our credentialed nursing faculty — not generated content.
                </p>
              </div>

              {/* Faculty Review Attribution */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex -space-x-2 overflow-hidden">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-teal-700 text-white font-bold text-[10px] border-2 border-[#0B2942]">RN</span>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-700 text-white font-bold text-[10px] border-2 border-[#0B2942]">NP</span>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-700 text-white font-bold text-[10px] border-2 border-[#0B2942]">DNP</span>
                </div>
                <p className="text-xs text-slate-300 font-medium">
                  <strong className="text-white font-bold">12 faculty</strong> reviewed this month's question bank
                </p>
              </div>
            </div>

            {/* Right Hero Stats Cards */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              <div className="bg-[#113859] p-5 rounded-2xl border border-white/10 shadow-inner flex flex-col justify-between">
                <span className="text-[10px] font-bold tracking-widest text-slate-300 uppercase">Exam Readiness</span>
                <div className="text-3xl font-extrabold text-white mt-1">{readinessPercent}%</div>
              </div>

              <div className="bg-[#113859] p-5 rounded-2xl border border-white/10 shadow-inner flex flex-col justify-between">
                <span className="text-[10px] font-bold tracking-widest text-slate-300 uppercase">Study Hours Logged</span>
                <div className="text-3xl font-extrabold text-white mt-1">{studyHours}h</div>
              </div>
            </div>

          </div>
        </div>

        {/* Dedicated Right Column for Ad Unit outside hero banner */}
        <div className="xl:col-span-4 flex flex-col justify-start">
          <AdUnit placement="dashboard_welcome_right" />
        </div>
      </div>

      {/* Trust Strip */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-xs">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-semibold text-slate-700">
          <div className="flex items-center gap-2.5">
            <Check size={16} className="text-[#0F7D6C] shrink-0" />
            <span>Graduates certified in all 50 states</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Check size={16} className="text-[#0F7D6C] shrink-0" />
            <span>Faculty: DNP · NP · RN · MSN · BSN</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Check size={16} className="text-[#0F7D6C] shrink-0" />
            <span>Content reviewed against state board handbooks</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Check size={16} className="text-[#0F7D6C] shrink-0" />
            <span>Hundreds of graduates nationwide</span>
          </div>
        </div>
      </div>

      {/* Section: Your Clinical Study Pathway */}
      <div className="space-y-4 pt-2">
        <div className="flex justify-between items-end">
          <div>
            <h2 className="text-lg font-serif font-bold text-slate-900">Your clinical study pathway</h2>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Structured like a course syllabus — complete each module in order.
            </p>
          </div>
          <button 
            onClick={() => onNavigate('guide')}
            className="text-xs font-bold text-[#0F7D6C] hover:text-[#0B6356] flex items-center gap-1 transition-colors"
          >
            <span>View full pathway</span>
            <ArrowRight size={14} />
          </button>
        </div>

        {/* 4 Pathway Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Card 1: Clinical Theory Guide */}
          <div 
            onClick={() => onNavigate('guide')}
            className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#0F7D6C]/50 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between h-full"
          >
            <div>
              <div className="w-9 h-9 rounded-xl bg-teal-50 text-[#0F7D6C] flex items-center justify-center mb-4 group-hover:bg-[#0F7D6C] group-hover:text-white transition-colors">
                <BookOpen size={18} />
              </div>
              <h3 className="font-bold text-sm text-slate-900 group-hover:text-[#0F7D6C] transition-colors">
                Clinical theory guide
              </h3>
              <p className="text-xs text-slate-500 font-normal mt-1.5 leading-relaxed">
                Core curriculum modules written by board-certified RN educators.
              </p>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-100">
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-1.5">
                <div className="bg-[#0F7D6C] h-full rounded-full" style={{ width: `${guideProgress}%` }}></div>
              </div>
              <div className="text-[11px] font-semibold text-slate-400">{guideProgress}% complete</div>
            </div>
          </div>

          {/* Card 2: Practice Quiz Drills */}
          <div 
            onClick={() => onNavigate('practice')}
            className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#0F7D6C]/50 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between h-full"
          >
            <div>
              <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4 group-hover:bg-emerald-700 group-hover:text-white transition-colors">
                <FileText size={18} />
              </div>
              <h3 className="font-bold text-sm text-slate-900 group-hover:text-[#0F7D6C] transition-colors">
                Practice quiz drills
              </h3>
              <p className="text-xs text-slate-500 font-normal mt-1.5 leading-relaxed">
                Peer-reviewed question sets by nursing pedagogy specialists.
              </p>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-100">
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-1.5">
                <div className="bg-[#0F7D6C] h-full rounded-full" style={{ width: `${practiceProgress}%` }}></div>
              </div>
              <div className="text-[11px] font-semibold text-slate-400">{practiceProgress}% complete</div>
            </div>
          </div>

          {/* Card 3: Board Simulation Exam */}
          <div 
            onClick={() => onNavigate('exam')}
            className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#0F7D6C]/50 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between h-full"
          >
            <div>
              <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center mb-4 group-hover:bg-amber-700 group-hover:text-white transition-colors">
                <GraduationCap size={18} />
              </div>
              <h3 className="font-bold text-sm text-slate-900 group-hover:text-[#0F7D6C] transition-colors">
                Board simulation exam
              </h3>
              <p className="text-xs text-slate-500 font-normal mt-1.5 leading-relaxed">
                Timed, official board-style mock exams with clinical rationales.
              </p>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-100">
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-1.5">
                <div className="bg-[#0F7D6C] h-full rounded-full" style={{ width: `${readinessPercent}%` }}></div>
              </div>
              <div className="text-[11px] font-semibold text-slate-400">{readinessPercent}% ready</div>
            </div>
          </div>

          {/* Card 4: Clinical Skills Lab */}
          <div 
            onClick={() => onNavigate('skills')}
            className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#0F7D6C]/50 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between h-full"
          >
            <div>
              <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center mb-4 group-hover:bg-sky-700 group-hover:text-white transition-colors">
                <Layers size={18} />
              </div>
              <h3 className="font-bold text-sm text-slate-900 group-hover:text-[#0F7D6C] transition-colors">
                Clinical skills lab
              </h3>
              <p className="text-xs text-slate-500 font-normal mt-1.5 leading-relaxed">
                Step-by-step procedures curated by RN instructors.
              </p>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-100">
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mb-1.5">
                <div className="bg-[#0F7D6C] h-full rounded-full" style={{ width: `${skillsProgress}%` }}></div>
              </div>
              <div className="text-[11px] font-semibold text-slate-400">{skillsProgress}% complete</div>
            </div>
          </div>

        </div>
      </div>

      {/* Faculty Callout Section (Soft Gold / Amber) */}
      <div className="bg-[#FEF3C7]/60 rounded-2xl p-6 border border-amber-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h3 className="font-serif font-bold text-slate-900 text-sm md:text-base">
            Meet the faculty behind your questions
          </h3>
          <p className="text-xs text-slate-600 font-normal leading-relaxed">
            Every rationale is written and signed off by a credentialed instructor — see who reviewed today's material.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="px-3 py-1 bg-white/90 text-amber-900 rounded-lg text-xs font-bold border border-amber-300 shadow-2xs">
              DNP
            </span>
            <span className="px-3 py-1 bg-white/90 text-amber-900 rounded-lg text-xs font-bold border border-amber-300 shadow-2xs">
              NP
            </span>
            <span className="px-3 py-1 bg-white/90 text-amber-900 rounded-lg text-xs font-bold border border-amber-300 shadow-2xs">
              RN
            </span>
            <span className="px-3 py-1 bg-white/90 text-amber-900 rounded-lg text-xs font-bold border border-amber-300 shadow-2xs">
              MSN
            </span>
          </div>

          <button
            onClick={() => onNavigate('faculty')}
            className="px-4 py-2 bg-amber-900 hover:bg-amber-950 text-white rounded-xl font-bold text-xs shadow-sm transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer"
          >
            <span>Meet Board Faculty</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* De-emphasized Gamification / Progress Metrics */}
      <div className="space-y-3 pt-2">
        <h2 className="text-sm font-bold text-slate-800">Your progress</h2>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-200">
            <div className="text-[10px] font-extrabold tracking-wider text-slate-400 uppercase">Daily Streak</div>
            <div className="text-xl md:text-2xl font-bold text-slate-900 mt-1">{currentStreak} days</div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200">
            <div className="text-[10px] font-extrabold tracking-wider text-slate-400 uppercase">Badges Earned</div>
            <div className="text-xl md:text-2xl font-bold text-slate-900 mt-1">{badgesCount}</div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200">
            <div className="text-[10px] font-extrabold tracking-wider text-slate-400 uppercase">Exams Complete</div>
            <div className="text-xl md:text-2xl font-bold text-slate-900 mt-1">{totalExams}</div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200">
            <div className="text-[10px] font-extrabold tracking-wider text-slate-400 uppercase">Highest Score</div>
            <div className="text-xl md:text-2xl font-bold text-slate-900 mt-1">{highestScore}%</div>
          </div>
        </div>
      </div>

      {/* Dashboard Bottom Ad Placement */}
      <div className="w-full pt-4">
        <AdUnit placement="dashboard_bottom" />
      </div>

    </div>
  );
};
